import React, { useEffect, useState } from 'react';
import { GetJson, PostJson } from '@awp/helpers/fetchReact';
import { FORM_MODE, Permissions, REST_API } from '@awp/constants';
import { Card, Container, Row } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import AwpSpinner from '@awp/components/Common/AwpSpinner';
import { Link, useNavigate } from 'react-router-dom';
import { StandardGraphAxisService } from '@components/StandardGraphAxis/StandardGraphAxisService';
import { createStandardgraphaxis, selectAllStandardgraphaxises } from '@stores/observationTypeStatusesSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AwpContainer } from '@components/Common/AwpContainer';
import Authorize from '@components/Authorize';
import { getListPageParams } from '@awp/helpers/navigation';
import isEqual from 'lodash/isEqual';

const StandardGraphAxisCreate = (props) => {
  const [unitsOfMeasureDropdown, setUnitsOfMeasureDropdown] = useState([]);
  const [entityErrors, setEntityErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enabledIsMinimumScaleFlexible, setEnabledIsMinimumScaleFlexible] = useState(false);
  const [enabledIsMaximumScaleFlexible, setEnabledIsMaximumScaleFlexible] = useState(false);
  const [initialDto, setInitialDto] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allStandardGraphAxises = useSelector(selectAllStandardgraphaxises);

  const listPageURL = getListPageParams();
  const standardGraphAxisService = new StandardGraphAxisService(FORM_MODE.CREATE);
  const { readOnly, title, buttonLabel, relativePath, successMessage, entityName } = standardGraphAxisService.crud;

  useEffect(() => {
    setLoading(true);
    GetJson(REST_API.UNIT_OF_MEASURES)
      .then((response) => response.json())
      .then((unitsOfMeasure) => {
        setUnitsOfMeasureDropdown(
          unitsOfMeasure?.map((u) => {
            return { value: u.Id, label: u.Name };
          }),
        );
        setInitialDto({
          IsMinimumScaleFlexible: true,
          IsMaximumScaleFlexible: true,
        });
        setLoading(false);
      });
  }, []);

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);

    createEntity(setSubmitting, setErrors, {
      ...standardGraphAxisService.mapFormToDTO({ entity: values }),
    });
  };

  const createEntity = (setSubmitting, setErrors, entityDto) => {
    PostJson(REST_API.STANDARD_GRAPH_AXIS, entityDto)
      .then(async (response) => {
        if (response.ok) {
          const responseData = await response.json();
          setInitialDto({
            ...standardGraphAxisService.mapInitialValues({ entity: responseData, unitsOfMeasureDropdown }),
          });
          dispatch(createStandardgraphaxis(responseData));
          toast.success(successMessage);
          navigate(`${relativePath}${FORM_MODE.VIEW}/${responseData.Id}`);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const onClickCancel = () => {
    navigate(`${relativePath}${listPageURL}`);
  };

  return (
    <>
      <AwpContainer>
        <Authorize requires={[Permissions.WriteAdminTools]}>
          {loading ? (
            <Container>
              <AwpSpinner />
            </Container>
          ) : (
            <>
              <Formik
                initialValues={{
                  IsMinimumScaleFlexible: true,
                  IsMaximumScaleFlexible: true,
                }}
                validateOnMount={true}
                validationSchema={StandardGraphAxisService.getValidations({}, allStandardGraphAxises)}
                onSubmit={handleSubmit}
              >
                {(props) => (
                  <Form>
                    <Link className="awp-text-btn" to={`${relativePath}${listPageURL}`}>
                      {`List ${entityName}`}
                    </Link>
                    <span className="mx-3">&gt;</span>
                    <span>{title}</span>
                    <Card border="light" className="page-card">
                      <Card.Header>
                        <Row className="mt-4"></Row>
                        {standardGraphAxisService.crud.getHeader()}
                      </Card.Header>
                      <Card.Body>
                        {StandardGraphAxisService.getFields({
                          enabledIsMinimumScaleFlexible,
                          enabledIsMaximumScaleFlexible,
                          setEnabledIsMinimumScaleFlexible,
                          setEnabledIsMaximumScaleFlexible,
                          props,
                          readOnly,
                          unitsOfMeasureDropdown,
                        })}
                      </Card.Body>
                      <Card.Footer>
                        <Row className="my-2">
                          <AwpFormActionButtons
                            title=" "
                            id={2}
                            entityName={entityName}
                            buttonLabel={buttonLabel}
                            mode={FORM_MODE.CREATE}
                            onClickCancel={onClickCancel}
                            permissions={[Permissions.WriteAdminTools]}
                            readOnly={readOnly}
                            isAlignLeft={true}
                            btnCancelName={'Close'}
                            props={props}
                            isPristine={isEqual(
                              standardGraphAxisService.mapFormToDTO({ entity: props.values }),
                              standardGraphAxisService.mapFormToDTO({ entity: initialDto }),
                            )}
                          />
                        </Row>
                      </Card.Footer>
                    </Card>
                  </Form>
                )}
              </Formik>
              <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
            </>
          )}
        </Authorize>
      </AwpContainer>
    </>
  );
};

export default StandardGraphAxisCreate;
